import { useState, useEffect, useCallback } from "react";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import TestAnswersEditor from "./TestAnswersEditor";
import ConversionChartEditor from "./ConversionChartEditor";
import TestInfoForm from "./TestInfoForm";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import api from "../../actprep-ui/utils/api";
import { testingVariants } from "../../actprep-ui/constants/testing";

export default function TestForm({ row: testRow, onEdit, onCreate }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [error, setError] = useState(null);
  const { data, refetch } = useGet(apiUrls.admin.tests(), {
    params: {
      search_params: {
        uuid: testRow?.uuid,
      },
      keep_related: true,
    },
    enabled: !!testRow?.uuid,
    staleTime: 0,
  });
  const test = data?.data[0];

  const isCreateMode = !testRow;
  const isTabDisabled = isCreateMode;

  const handleSubmitAnswers = useCallback(
    async (updatedAnswers) => {
      const updatedTest = {
        answers: updatedAnswers,
      };
      await api.put(apiUrls.admin.tests(testRow.uuid), updatedTest);
      refetch();
      showSuccessSnackbar("Test answers updated successfully!");
    },
    [refetch, testRow]
  );

  const handleSubmitConversionChart = useCallback(
    async (subject, updatedChart) => {
      const updatedTest = {
        conversion_chart: {
          ...test.conversion_chart,
          [subject]: updatedChart,
        },
      };
      await api.put(apiUrls.admin.tests(testRow.uuid), updatedTest);
      refetch();
      showSuccessSnackbar("Conversion chart updated successfully!");
    },
    [refetch, testRow, test]
  );

  const handleCreate = async (formData) => {
    const response = await api.post(apiUrls.admin.tests(), formData);
    if (response.status != 200) {
      return;
    }
    const { success, reason } = response.data;
    if (!success) {
      setError(reason);
      return;
    }
    showSuccessSnackbar("Test created successfully!");
    refetch();
    if (onCreate) {
      onCreate(response.data.data);
    }
  };

  const handleEdit = async (uuid, formData) => {
    const response = await api.put(apiUrls.admin.tests(uuid), formData);
    if (response.status != 200) {
      return;
    }
    const { success, reason } = response.data;
    if (!success) {
      setError(reason);
      return;
    }
    showSuccessSnackbar("Test updated successfully!");
    refetch();
    if (onEdit) {
      onEdit(response.data.data);
    }
  };

  useEffect(() => {
    if (testRow) {
      refetch();
    }
  }, [testRow, refetch]);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
        <Tabs
          value={tabIndex}
          onChange={(event, newValue) => setTabIndex(newValue)}
          centered
        >
          <Tab label="Info" />
          <Tab label="Answers" disabled={isTabDisabled} />
          {test?.variant != testingVariants.LESSON && (
            <Tab label="Conversion Chart" disabled={isTabDisabled} />
          )}
        </Tabs>
      </Box>
      {tabIndex === 0 && (
        <TestInfoForm
          test={testRow}
          error={error}
          setError={setError}
          isCreateMode={isCreateMode}
          onCreate={handleCreate}
          onEdit={handleEdit}
        />
      )}
      {tabIndex === 1 && (
        <TestAnswersEditor test={test} onSubmit={handleSubmitAnswers} />
      )}
      {tabIndex === 2 && (
        <ConversionChartEditor
          test={test}
          onSubmit={handleSubmitConversionChart}
        />
      )}
    </>
  );
}
